'use client'

import { useCallback } from 'react'

import { clearAllRecentActivity } from '~/components/QuickSearch/hooks/useRecentActivity'
import { PERSIST_ROOT_STORAGE_KEY, persistor } from '~/store'

import reloadPage from './reloadPage'

/**
 * Enters or exits demo mode via a full page reload.
 *
 * The reload is intentional — it is the only way to guarantee no real customer data
 * survives anywhere in memory when entering demo mode: RTK Query caches, page-level
 * slices, QuickSearch state, and mounted component state are all rebuilt from scratch,
 * with every response passing through the anonymizer (`anonymizingFetchBaseQuery`).
 *
 * Recent activity (Command-K history) is cleared on both enter and exit so real names
 * never leak into a demo session, and anonymized labels never linger after exiting.
 * Clearing runs only after the demo flag is successfully persisted, so a failed
 * parse/write cannot wipe history while leaving demo mode unchanged.
 *
 * The flag is written straight to redux-persist storage instead of being dispatched:
 * flipping the live Redux tree first would swap DataManager's IndexedDB name mid-session
 * and let the entity managers persist the previous mode's snapshots into the new
 * database (surfacing real PII at the start of the next demo load). The persistor is
 * paused first so a queued flush of the current (old-flag) state can't clobber the
 * value we write before the reload lands.
 */
export default function useSetDemoMode() {
  return useCallback(async (enabled: boolean) => {
    persistor.pause()

    const raw = window.localStorage.getItem(PERSIST_ROOT_STORAGE_KEY)
    const root: Record<string, string> = raw ? JSON.parse(raw) : {}
    root.demoMode = JSON.stringify({ enabled })
    window.localStorage.setItem(PERSIST_ROOT_STORAGE_KEY, JSON.stringify(root))

    clearAllRecentActivity()

    reloadPage()
  }, [])
}
